import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"
import { requireInventoryUserId } from "../lib/auth.server"
import { buildUnitsCsv } from "../lib/export-csv"
import { buildUnitsExcel } from "../lib/export-excel"
import { buildUnitsPdfSummary } from "../lib/export-pdf"
import { describeFilters, fetchExportRows } from "../lib/export-rows"
import { UNIT_STATUSES } from "../lib/status"

const unitStatusSchema = z.enum(UNIT_STATUSES)

const exportFiltersSchema = z
  .object({
    type: z.enum(["sim", "device"]).optional(),
    status: unitStatusSchema.optional(),
    carrierId: z.number().optional(),
    vendorId: z.number().optional(),
    productId: z.number().optional(),
    serialQuery: z.string().optional(),
    filtersDescription: z.string().optional(),
  })
  .optional()

export type ExportPayload = {
  filename: string
  contentType: string
  base64: string
}

function toPayload(
  filename: string,
  contentType: string,
  buffer: ArrayBuffer,
): ExportPayload {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return {
    filename,
    contentType,
    base64: btoa(binary),
  }
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replaceAll(":", "-")
}

export const exportUnitsCsvFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchExportRows(filters)
    const csv = buildUnitsCsv(rows)
    const encoded = new TextEncoder().encode(csv)
    const buffer = encoded.buffer.slice(
      encoded.byteOffset,
      encoded.byteOffset + encoded.byteLength,
    ) as ArrayBuffer
    return toPayload(
      `inventory-units-${stamp()}.csv`,
      "text/csv;charset=utf-8",
      buffer,
    )
  })

export const exportUnitsExcelFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchExportRows(filters)
    const buffer = buildUnitsExcel(rows)
    return toPayload(
      `inventory-units-${stamp()}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      buffer,
    )
  })

export const exportUnitsPdfSummaryFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => exportFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const filters = data ?? {}
    const rows = await fetchExportRows(filters)
    const generatedAt = Date.now()
    const filtersDescription =
      filters.filtersDescription?.trim() || describeFilters(filters)
    const buffer = await buildUnitsPdfSummary(rows, {
      generatedAt,
      filtersDescription,
    })
    return toPayload(
      `inventory-summary-${stamp()}.pdf`,
      "application/pdf",
      buffer,
    )
  })
